/**
 * Fallback Low H.264 remux when the phone cannot live-encode.
 * Plays the clip back and encodes it. Live capture (liveH264Capture) skips this.
 */

import {
  canUseWebCodecsReelEncoder,
  pickAvcConfig,
} from "./reelWebCodecsEncoder";
import {
  EVAL_VIDEO_BITRATES,
  EVAL_VIDEO_CAPTURE,
  blobWithCanonicalEvalMime,
  canonicalEvalVideoMime,
} from "./evalVideoEncoding";

/** ~8 MB/min Low target × 1.5 — same ceiling as iOS / server oversize check. */
export const EVAL_LOW_BYTES_PER_MIN_CEILING = 12 * 1024 * 1024;

function yieldToUi() {
  const scheduler =
    typeof window !== "undefined" && window.scheduler ? window.scheduler : null;
  if (scheduler && typeof scheduler.yield === "function") {
    return scheduler.yield();
  }
  return new Promise((resolve) => {
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => resolve());
      return;
    }
    setTimeout(resolve, 0);
  });
}

function probeBlobDuration(blob) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";
    const finish = (result) => {
      video.onloadedmetadata = null;
      video.onerror = null;
      URL.revokeObjectURL(url);
      resolve(result);
    };
    const timer = window.setTimeout(() => finish(null), 8000);
    video.onloadedmetadata = () => {
      window.clearTimeout(timer);
      const duration = Number(video.duration);
      finish({
        duration: Number.isFinite(duration) && duration > 0 ? duration : 0,
        width: video.videoWidth || 0,
        height: video.videoHeight || 0,
      });
    };
    video.onerror = () => {
      window.clearTimeout(timer);
      finish(null);
    };
    video.src = url;
  });
}

export function alreadyLowH264(blob, durationSec) {
  const mime = canonicalEvalVideoMime(blob?.type);
  if (mime !== "video/mp4") return false;
  const duration = Number(durationSec);
  if (!(duration >= 1)) return blob.size <= 6 * 1024 * 1024;
  const bytesPerMin = blob.size / (duration / 60);
  return bytesPerMin <= EVAL_LOW_BYTES_PER_MIN_CEILING;
}

/** Low remux frame rate: 60 when the encoder accepts it, otherwise 30. */
export async function pickEvalLowTranscodeFps(width, height, bitrate) {
  if (await pickAvcConfig(width, height, 60, bitrate)) return 60;
  if (await pickAvcConfig(width, height, 30, bitrate)) return 30;
  return null;
}

function fitCoverRect(srcW, srcH, dstW, dstH) {
  const scale = Math.max(dstW / Math.max(1, srcW), dstH / Math.max(1, srcH));
  const dw = srcW * scale;
  const dh = srcH * scale;
  return {
    sx: 0,
    sy: 0,
    sw: srcW,
    sh: srcH,
    dx: (dstW - dw) / 2,
    dy: (dstH - dh) / 2,
    dw,
    dh,
  };
}

/**
 * @param {Blob} blob
 * @returns {Promise<{ blob: Blob, encoded: boolean, encodeWallMs: number|null, mimeType: string }>}
 */
export async function transcodeEvalClipToLowH264(blob) {
  if (!blob || blob.size < 1024) {
    return {
      blob,
      encoded: false,
      encodeWallMs: null,
      mimeType: canonicalEvalVideoMime(blob?.type),
    };
  }
  const probe = await probeBlobDuration(blob);
  const duration = probe?.duration || 0;
  const sourceMime = canonicalEvalVideoMime(blob.type);
  if (alreadyLowH264(blob, duration)) {
    return {
      blob: blobWithCanonicalEvalMime(blob),
      encoded: false,
      encodeWallMs: null,
      mimeType: "video/mp4",
    };
  }

  if (!canUseWebCodecsReelEncoder() || !(duration > 0.4)) {
    // Do not upload WebM/VP8/VP9 — server would re-encode again.
    if (sourceMime !== "video/mp4") {
      throw new Error(
        "This phone cannot encode H.264 MP4 for upload. Try Safari or update Chrome."
      );
    }
    return {
      blob: blobWithCanonicalEvalMime(blob),
      encoded: false,
      encodeWallMs: null,
      mimeType: "video/mp4",
    };
  }

  const url = URL.createObjectURL(blob);
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.src = url;
  const encodeStarted = Date.now();

  try {
    await new Promise((resolve, reject) => {
      const timer = window.setTimeout(
        () => reject(new Error("eval remux load timeout")),
        12000
      );
      video.onloadeddata = () => {
        window.clearTimeout(timer);
        resolve();
      };
      video.onerror = () => {
        window.clearTimeout(timer);
        reject(new Error("eval remux load failed"));
      };
    });
    await yieldToUi();

    const srcW = video.videoWidth || EVAL_VIDEO_CAPTURE.width;
    const srcH = video.videoHeight || EVAL_VIDEO_CAPTURE.height;
    const dstW = EVAL_VIDEO_CAPTURE.width;
    const dstH = EVAL_VIDEO_CAPTURE.height;
    const canvas = document.createElement("canvas");
    canvas.width = dstW;
    canvas.height = dstH;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) {
      throw new Error("eval remux canvas unavailable");
    }
    const fit = fitCoverRect(srcW, srcH, dstW, dstH);
    const bitrate = EVAL_VIDEO_BITRATES.low;
    const fps = await pickEvalLowTranscodeFps(dstW, dstH, bitrate);
    const config = fps
      ? await pickAvcConfig(dstW, dstH, fps, bitrate)
      : null;
    if (!config) {
      throw new Error("eval remux H.264 encoder unavailable");
    }

    const { Muxer, ArrayBufferTarget } = await import("mp4-muxer");
    const target = new ArrayBufferTarget();
    const muxer = new Muxer({
      target,
      video: {
        codec: "avc",
        width: dstW,
        height: dstH,
        frameRate: fps,
      },
      fastStart: "in-memory",
      firstTimestampBehavior: "offset",
    });

    const VideoEncoder = window.VideoEncoder;
    const VideoFrame = window.VideoFrame;
    let encoderError = null;
    const encoder = new VideoEncoder({
      output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
      error: (err) => {
        encoderError = err;
      },
    });
    encoder.configure(config);

    const frameDurationUs = Math.round(1e6 / fps);
    const step = 1 / fps;
    const targetFrames = Math.max(1, Math.round(duration * fps));
    let frameIndex = 0;
    let nextTime = 0;
    const closeEncoder = () => {
      try {
        encoder.close();
      } catch {
        /* already closed */
      }
    };

    const encodePaintedFrame = () => {
      const timestamp = frameIndex * frameDurationUs;
      const frame = new VideoFrame(canvas, {
        timestamp,
        duration: frameDurationUs,
      });
      try {
        encoder.encode(frame, { keyFrame: frameIndex % fps === 0 });
      } finally {
        frame.close();
      }
      frameIndex += 1;
      nextTime += step;
    };

    const paintCanvas = () => {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, dstW, dstH);
      ctx.drawImage(video, fit.sx, fit.sy, fit.sw, fit.sh, fit.dx, fit.dy, fit.dw, fit.dh);
    };

    const paintAndEncode = async () => {
      if (encoderError) throw encoderError;
      const t = video.currentTime || 0;
      if (t + 0.0005 < nextTime || frameIndex >= targetFrames) return;
      paintCanvas();
      let guard = 0;
      while (
        frameIndex < targetFrames &&
        t + 0.0005 >= nextTime &&
        guard < 3
      ) {
        encodePaintedFrame();
        guard += 1;
        if (encoder.encodeQueueSize > 8) break;
      }
      if (encoder.encodeQueueSize > 8) {
        await new Promise((resolve) => {
          const onDequeue = () => {
            if (encoder.encodeQueueSize <= 4) {
              encoder.removeEventListener("dequeue", onDequeue);
              resolve();
            }
          };
          encoder.addEventListener("dequeue", onDequeue);
          window.setTimeout(resolve, 40);
        });
      }
      await yieldToUi();
    };

    try {
      await new Promise((resolve, reject) => {
        let settled = false;
        let painting = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          try {
            video.pause();
          } catch {
            /* ignore */
          }
          resolve();
        };
        const fail = (err) => {
          if (settled) return;
          settled = true;
          reject(err);
        };

        const onFrame = () => {
          if (settled || painting) return;
          painting = true;
          paintAndEncode()
            .then(() => {
              painting = false;
              if (settled) return;
              if (typeof video.requestVideoFrameCallback === "function") {
                video.requestVideoFrameCallback(onFrame);
              }
            })
            .catch((err) => {
              painting = false;
              fail(err);
            });
        };

        video.addEventListener("ended", finish, { once: true });
        video.addEventListener(
          "error",
          () => fail(new Error("eval remux playback failed")),
          { once: true }
        );
        window.setTimeout(
          finish,
          Math.max(8000, duration * 1100 + 4000)
        );

        if (typeof video.requestVideoFrameCallback === "function") {
          video.requestVideoFrameCallback(onFrame);
        } else {
          const poll = () => {
            if (settled) return;
            painting = true;
            paintAndEncode()
              .then(() => {
                painting = false;
                if (settled || video.ended) {
                  finish();
                  return;
                }
                window.setTimeout(poll, 40);
              })
              .catch((err) => {
                painting = false;
                fail(err);
              });
          };
          poll();
        }

        video.playbackRate = 1;
        const playResult = video.play();
        if (playResult && typeof playResult.catch === "function") {
          playResult.catch(() => fail(new Error("eval remux play failed")));
        }
      });

      if (frameIndex < 2) {
        closeEncoder();
        throw new Error("eval remux produced too few frames");
      }
      await encoder.flush();
      muxer.finalize();
      closeEncoder();
      if (encoderError) throw encoderError;
    } catch (err) {
      closeEncoder();
      throw err;
    }
    const encoded = new Blob([target.buffer], { type: "video/mp4" });
    encoded._smartttExt = "mp4";
    if (encoded.size < 1024) {
      throw new Error("eval remux produced empty file");
    }
    return {
      blob: encoded,
      encoded: true,
      encodeWallMs: Date.now() - encodeStarted,
      mimeType: "video/mp4",
    };
  } catch (err) {
    console.warn("Eval Low H.264 remux failed:", err?.message || err);
    if (sourceMime !== "video/mp4") {
      throw err instanceof Error
        ? err
        : new Error(String(err?.message || err || "eval remux failed"));
    }
    return {
      blob: blobWithCanonicalEvalMime(blob),
      encoded: false,
      encodeWallMs: null,
      mimeType: "video/mp4",
    };
  } finally {
    try {
      video.pause();
      video.removeAttribute("src");
      video.load();
    } catch {
      /* ignore */
    }
    URL.revokeObjectURL(url);
  }
}
